import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SparklesLogo from './SparklesLogo';
import './ChatMessage.css';

interface ChatMessageProps {
  message: {
    role: 'user' | 'assistant';
    content: string;
  };
  isLoading?: boolean;
  onRetry?: () => void;
  onEdit?: (content: string) => void;
}

export default function ChatMessage({ message, isLoading = false, onRetry, onEdit }: ChatMessageProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [rated, setRated] = useState<'up' | 'down' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);
  const editRef = useRef<HTMLTextAreaElement>(null);

  // Focus and auto-resize textarea when entering edit mode
  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      editRef.current.style.height = 'auto';
      editRef.current.style.height = editRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = message.content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEditStart = () => {
    setEditText(message.content);
    setIsEditing(true);
  };

  const handleEditSubmit = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== message.content && onEdit) {
      onEdit(trimmed);
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditText(message.content);
    setIsEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSubmit();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditText(e.target.value);
    // Auto-resize
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  return (
    <div className={`chat-message ${message.role}`}>
      {message.role === 'assistant' && (
        <div className="message-logo">
          <SparklesLogo size={24} />
        </div>
      )}

      <div className={`message-content ${isEditing ? 'editing' : ''}`}>
        {isLoading ? (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        ) : (
          <>
            {/* Edit mode for user messages */}
            {isEditing ? (
              <div className="message-edit">
                <textarea
                  ref={editRef}
                  value={editText}
                  onChange={handleEditChange}
                  onKeyDown={handleEditKeyDown}
                  className="message-edit-textarea"
                  rows={1}
                />
                <div className="message-edit-actions">
                  <button className="edit-cancel-btn" onClick={handleEditCancel}>
                    {t('agents.actionCancel')}
                  </button>
                  <button
                    className="edit-submit-btn"
                    onClick={handleEditSubmit}
                    disabled={!editText.trim() || editText.trim() === message.content}
                  >
                    {t('agents.actionSubmitEdit')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="message-text">
                  {message.role === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({node, ...props}) => <p className="markdown-paragraph" {...props} />,
                        ul: ({node, ...props}) => <ul className="markdown-list" {...props} />,
                        ol: ({node, ...props}) => <ol className="markdown-list" {...props} />,
                        li: ({node, ...props}) => <li className="markdown-list-item" {...props} />,
                        strong: ({node, ...props}) => <strong className="markdown-bold" {...props} />,
                        em: ({node, ...props}) => <em className="markdown-italic" {...props} />,
                        code: ({node, className, ...props}) =>
                          className ?
                            <code className="markdown-code-block" {...props} /> :
                            <code className="markdown-inline-code" {...props} />,
                        h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
                        h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
                        h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
                        blockquote: ({node, ...props}) => <blockquote className="markdown-blockquote" {...props} />,
                        table: ({node, ...props}) => <table className="markdown-table" {...props} />,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    <p>{message.content}</p>
                  )}
                </div>

                {/* Action buttons — compact row below message */}
                <div className="message-actions">
                  {message.role === 'user' ? (
                    <>
                      {onRetry && (
                        <button className="action-btn" onClick={onRetry} title={t('agents.actionRetry')}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" /></svg>
                        </button>
                      )}
                      {onEdit && (
                        <button className="action-btn" onClick={handleEditStart} title={t('agents.actionEdit')}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                      )}
                      <button className={`action-btn ${copied ? 'active' : ''}`} onClick={handleCopy} title={t('agents.actionCopy')}>
                        {copied ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className={`action-btn ${copied ? 'active' : ''}`} onClick={handleCopy} title={t('agents.actionCopy')}>
                        {copied ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                        )}
                      </button>
                      <button className={`action-btn ${rated === 'up' ? 'active' : ''}`} onClick={() => setRated(rated === 'up' ? null : 'up')} title={t('agents.actionThumbsUp')}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill={rated === 'up' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" /></svg>
                      </button>
                      <button className={`action-btn ${rated === 'down' ? 'active' : ''}`} onClick={() => setRated(rated === 'down' ? null : 'down')} title={t('agents.actionThumbsDown')}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill={rated === 'down' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" /></svg>
                      </button>
                      {onRetry && (
                        <button className="action-btn" onClick={onRetry} title={t('agents.actionRetry')}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" /></svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
